/* src/api/masterGroups.js (new version) */
import dynamicAPI                         from './dynamicAPI';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useMessage }                     from '../context/AdminMessageContext';

const url      = '/api/auth/masterGroups';
const queryKey = 'masterGroups';

export default function useMasterGroupsHook ({
                                                 page  = 1,
                                                 id,
                                                 q     = '',
                                                 limit = 25
                                             } = {}) {

    const qc = useQueryClient();
    const { setSuccessMessage, setErrorMessage } = useMessage();

    /* ────────────────────────────── QUERIES ────────────────────────────── */

    /* list */
    const getMasterGroups = useQuery(
        [queryKey, page, q, limit],
        async () => {
            const res = await dynamicAPI('get', `${url}?page=${page}&q=${q}&limit=${limit}`);
            if (res.error) {
                setErrorMessage(res.error || 'Unable to fetch groups.');
            }
            return res;
        },
        { retry: 0, staleTime: 15_000 }
    );

    /* single */
    const getMasterGroupById = useQuery(
        [queryKey, id],
        async () => {
            const res = await dynamicAPI('get', `${url}/${id}`);
            if (res.error) {
                setErrorMessage(res.error || 'Unable to fetch group.');
            }
            return res;
        },
        { retry: 0, enabled: !!id }
    );

    /* ──────────────────────────── MUTATIONS ───────────────────────────── */

    /* create */
    const postMasterGroup = useMutation(
        async (body) => await dynamicAPI('post', url, body),
        {
            retry: 0,
            onSuccess: (res) => {
                if (res.isSuccess) {
                    setSuccessMessage('Group created.');
                    qc.invalidateQueries([queryKey]);
                } else {
                    setErrorMessage(res.error || 'Failed to create group.');
                }
            },
            onError:  (err) => {
                const msg = err.message || err.error || 'Request failed.';
                setErrorMessage(msg);
            }
        }
    );

    /* update */
    const updateMasterGroup = useMutation(
        async (body) => await dynamicAPI('put', `${url}/${body._id}`, body),
        {
            retry: 0,
            onSuccess: (res) => {
                if (res.isSuccess) {
                    setSuccessMessage('Group updated.');
                    qc.invalidateQueries([queryKey]);
                } else {
                    setErrorMessage(res.error || 'Failed to update group.');
                }
            },
            onError: (err) => {
                const msg = err.message || err.error || 'Request failed.';
                setErrorMessage(msg);
            }
        }
    );

    /* delete */
    const deleteMasterGroup = useMutation(
        async (groupId) => await dynamicAPI('delete', `${url}/${groupId}`),
        {
            retry: 0,
            onSuccess: (res) => {
                if (res.isSuccess) {
                    setSuccessMessage('Group deleted.');
                    qc.invalidateQueries([queryKey]);
                } else {
                    setErrorMessage(res.error || 'Failed to delete group.');
                }
            },
            onError: (err) => {
                const msg = err.message || err.error || 'Request failed.';
                setErrorMessage(msg);
            }
        }
    );

    /* expose everything */
    return {
        getMasterGroups,
        getMasterGroupById,
        postMasterGroup,
        updateMasterGroup,
        deleteMasterGroup,
    };
}
